import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { Product } from 'src/app/models/interface/product/product';
import { OrderService } from 'src/app/services/api/orders/specific-order/order.service';
import { ProductDetailService } from '../../../../product-detail.service';
import { ActivateComponent } from './activate/activate.component';
import { CancelComponent } from './cancel/cancel.component';
import { InstallComponent } from './install/install.component';
import { TerminateComponent } from './terminate/terminate.component';
import { ViewChild, ElementRef } from '@angular/core';
import { NetworkService } from 'src/app/services/api/network/network.service';
import { NotificationLevel, NotificationsService } from 'src/app/services/api/notifications/notifications.service';
import { TranslateService } from '@ngx-translate/core';
import { JsonComponent } from 'src/app/core/json/json.component';
import { RetryComponent } from './retry/retry.component';
import { ERROR_OPERATIONAL_STATES } from 'src/app/utils/Constants';
import { AppService } from 'src/app/app.service';
import { MasMediaTvService } from 'src/app/services/api/mas-media-tv/mas-media-tv.service';
import { TvPackagesComponent } from './tv-packages/tv-packages.component';
import { PackagesResponse } from 'src/app/models/interface/tv/packagesResponse';

@Component({
  selector: 'app-product-depth-level-product-actions',
  templateUrl: './product-depth-level-product-actions.component.html',
  styleUrls: ['./product-depth-level-product-actions.component.scss']
})
export class ProductDepthLevelProductActionsComponent implements OnInit, OnDestroy {

  private _item: Product;
  hasCfses: boolean;

  @Input()
  get item(): Product {
    return this._item;
  }

  set item(value: Product) {
    this._item = value;
  }

  terminable: boolean;
  cancellable: boolean;
  modifiable: boolean;
  isWholesale: boolean;
  isWholesaleCorp: boolean;
  hideEventLogButton: boolean;

  territoryOwner: string;
  portalOwner: string;

  private serviceId: number;
  private type: string;
  private subscriptions = new Subscription();

  constructor(
    private dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private productDetailService: ProductDetailService,
    private orderService: OrderService,
    private networkService: NetworkService,
    private notificationsService: NotificationsService,
    private translateService: TranslateService,
    private appService: AppService,
    private masMediaTvService: MasMediaTvService
  ) {
  }

  ngOnInit() {
    this.hasCfses = this._item.cfses.length > 0;
    this.subscriptions.add(this.activatedRoute.queryParamMap.subscribe(params => {
      this.serviceId = +params.get('map');
      this.type = !params.get('type') ? 'product' : params.get('type');
      this.portalOwner = this.appService.settings.portalOwner;
      this.isWholesale = this.item.specId.includes('Wholesale') ? true : false;
      this.isWholesaleCorp = this.item.specId.includes('Wholesale_Corporate') ? true : false;

      const hiddenSpecs: string[] = this.appService.settings.hideEventLogButtonForSpecs || [];
      this.hideEventLogButton = hiddenSpecs.includes(this.item.specId);

      if(this.hasCfses){
        const territoryOwnerChar = this.item.cfses[0].cfsCharValues.find(serviceCharValue => serviceCharValue.specId == 'sc_network_territory_owner');
        if (territoryOwnerChar) {
          this.territoryOwner = territoryOwnerChar.value;
        } else {
          this.territoryOwner = this.portalOwner;
        }
      }
    }));
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  getEventLog() {
    this.networkService.getEventLog(this.item.cfses[0].serviceId).subscribe(
      (data) => {
        this.dialog.open(JsonComponent, {
          data: { title: this.translateService.instant('EVENT_LOG_PRODUCT.title'), json: data },
          disableClose: false,
          minWidth: '850px',
          restoreFocus: false,
          autoFocus: false
        });
      },
      (error) => {
        this.notificationsService.showNotification(this.translateService.instant('EVENT_LOG_PRODUCT.error'), NotificationLevel.ERROR);
      }
    );
  }

  cancel() {
    const matDialogRef = this.dialog.open(CancelComponent,
      {
        data: {
          item: this.item,
          type: this.type,
        },
        disableClose: true,
        minWidth: '500px',
        restoreFocus: false,
        autoFocus: false
      });
    this.subscriptions.add(matDialogRef.afterClosed()
      .pipe(
        filter(result => !!result)
      )
      .subscribe(() => {
        this.productDetailService.reload();
      }));
  }

  activate() {
    const matDialogRef = this.dialog.open(ActivateComponent,
      {
        data: {
          item: this.item,
          type: this.type,
        },
        disableClose: true,
        minWidth: '500px',
        restoreFocus: false,
        autoFocus: false
      });

    this.subscriptions.add(matDialogRef.afterClosed()
      .pipe(
        filter(result => !!result)
      )
      .subscribe(() => {
        this.productDetailService.reload();
      }));
  }

  install() {
    const matDialogRef = this.dialog.open(InstallComponent,
      {
        data: {
          item: this.item,
          type: this.type,
        },
        disableClose: true,
        minWidth: '500px',
        restoreFocus: false,
        autoFocus: false
      });

    this.subscriptions.add(matDialogRef.afterClosed()
      .pipe(
        filter(result => !!result)
      )
      .subscribe(() => {
        this.productDetailService.reload();
      }));
  }

  terminate() {
    const matDialogRef = this.dialog.open(TerminateComponent,
      {
        data: {
          item: this.item,
          type: this.type,
        },
        disableClose: true,
        minWidth: '500px',
        restoreFocus: false,
        autoFocus: false
      });

    this.subscriptions.add(matDialogRef.afterClosed()
      .pipe(
        filter(result => !!result)
      )
      .subscribe(() => {
        this.productDetailService.reload();
      }));
  }

  retry() {
    const matDialogRef = this.dialog.open(RetryComponent,
      {
        data: {
          item: this.item,
          type: this.type,
        },
        disableClose: true,
        minWidth: '500px',
        restoreFocus: false,
        autoFocus: false
      });

    this.subscriptions.add(matDialogRef.afterClosed()
      .pipe(
        filter(result => !!result)
      )
      .subscribe(() => {
        this.productDetailService.reload();
      }));
  }

  isRetryButtonVisible() {
    let state = this.hasCfses ? this.item.cfses[0].operationalState: undefined;
    if (ERROR_OPERATIONAL_STATES.find(operationalState => operationalState.value === state)) {
      return true;
    }
    else {
      return false;
    }
  }

  isButtonVisibleByStates(states: string[]){
    let state = this.hasCfses ? this.item.cfses[0].serviceState: undefined;
    return states.includes(state) && this.isWholesale
  }

  getActiveTvPackages() {
    const email = this.item.productCharValues.find(charValue => charValue.specId === 'pc_tv_ott_user_email').value;
    const profile = this.item.productCharValues.find(charValue => charValue.specId === 'pc_tv_ott_profile').value;

    this.masMediaTvService.getActiveTvPackages(email, profile).subscribe(
      (data: PackagesResponse) => {
        if(data.codResultado === "000"){
          this.dialog.open(TvPackagesComponent, {
            data: { title: this.translateService.instant('TV_PACKAGE_PRODUCT.title'), packages: data.packages},
            disableClose: false,
            maxWidth: '60vw'
          });
        } else {
          this.notificationsService.showNotification(this.translateService.instant('TV_PACKAGE_PRODUCT.error'), NotificationLevel.ERROR);
        }
      },
      (error) => {
        this.notificationsService.showNotification(this.translateService.instant('TV_PACKAGE_PRODUCT.error'), NotificationLevel.ERROR);
      }
    );
  }

}

